/**
 * SuperAdmin 전용 — 플랫폼의 모든 사용자 관리.
 *
 * 기능:
 *   - 검색 / 페이지네이션
 *   - 이메일 인증 강제 처리
 *   - 모든 세션 강제 로그아웃
 *   - 계정 비활성화 / 해제
 *   - SuperAdmin 권한 부여 / 회수
 *
 * 안전장치:
 *   - 본인 계정은 비활성화 못 함 (서버에서도 거부)
 *   - 마지막 SuperAdmin 권한 회수 거부 (서버 측)
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Ban,
  CheckCircle2,
  KeyRound,
  Loader2,
  LogOut,
  MailCheck,
  Search,
  ShieldCheck,
  Users,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import {
  adminRevokeSessions,
  adminSuspend,
  adminToggleSuperAdmin,
  adminUnsuspend,
  adminVerifyEmail,
  getAdminUserDetail,
  listAdminUsers,
  type AdminUserDetail,
  type AdminUserListItem,
} from "@/lib/console-api";
import { extractErrorMessage } from "@/lib/api-client";
import { useAuthStore } from "@/stores/auth-store";

const PAGE_SIZE = 30;

export function AdminUsersPage(): JSX.Element {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [activeUserId, setActiveUserId] = useState<string | null>(null);

  const usersQuery = useQuery({
    queryKey: ["admin-users", { page, q }],
    queryFn: () => listAdminUsers({ page, pageSize: PAGE_SIZE, q: q || undefined }),
  });

  const onSearch = (e: React.FormEvent): void => {
    e.preventDefault();
    setQ(searchInput.trim());
    setPage(1);
  };

  const items = usersQuery.data?.items ?? [];
  const total = usersQuery.data?.total ?? 0;
  const totalPages = total === 0 ? 0 : Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-6 p-8">
      <header>
        <p className="text-sm text-muted-foreground">관리</p>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Users className="h-6 w-6" aria-hidden /> 사용자 관리
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          devit 에 가입한 모든 사용자를 조회하고 운영 액션을 수행합니다.
        </p>
      </header>

      <form onSubmit={onSearch} className="flex gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            placeholder="이메일 / 이름 검색"
            className="pl-9"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <Button type="submit" variant="outline">검색</Button>
        {q && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setSearchInput("");
              setQ("");
              setPage(1);
            }}
          >
            초기화
          </Button>
        )}
      </form>

      {usersQuery.isLoading && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> 불러오는 중…
        </p>
      )}

      {!usersQuery.isLoading && items.length === 0 && (
        <p className="rounded-lg border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
          {q ? `"${q}" 와 일치하는 사용자가 없습니다.` : "등록된 사용자가 없습니다."}
        </p>
      )}

      {items.length > 0 && (
        <>
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">이메일</th>
                  <th className="px-4 py-3 text-left font-medium">이름</th>
                  <th className="px-4 py-3 text-left font-medium">상태</th>
                  <th className="px-4 py-3 text-left font-medium">조직</th>
                  <th className="px-4 py-3 text-left font-medium">가입일</th>
                  <th className="px-4 py-3 text-right font-medium">관리</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((u) => (
                  <UserRow key={u.id} user={u} onClick={() => setActiveUserId(u.id)} />
                ))}
              </tbody>
            </table>
          </div>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm">
              <p className="text-muted-foreground">
                {total}명 중 {(page - 1) * PAGE_SIZE + 1}–
                {Math.min(page * PAGE_SIZE, total)}
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  이전
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  다음
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {activeUserId && (
        <UserDetailDialog
          userId={activeUserId}
          onClose={() => setActiveUserId(null)}
        />
      )}
    </div>
  );
}

function UserRow({
  user,
  onClick,
}: {
  user: AdminUserListItem;
  onClick: () => void;
}): JSX.Element {
  return (
    <tr className="hover:bg-accent/30">
      <td className="px-4 py-3">
        <span className="font-medium">{user.email}</span>
      </td>
      <td className="px-4 py-3 text-muted-foreground">{user.name ?? "—"}</td>
      <td className="px-4 py-3">
        <div className="flex flex-wrap gap-1">
          {user.suspendedAt && (
            <span className="rounded bg-destructive/10 px-1.5 py-0.5 text-xs text-destructive">
              비활성
            </span>
          )}
          {user.emailVerified ? (
            <span className="rounded bg-emerald-500/10 px-1.5 py-0.5 text-xs text-emerald-600 dark:text-emerald-400">
              인증됨
            </span>
          ) : (
            <span className="rounded bg-orange-500/10 px-1.5 py-0.5 text-xs text-orange-600 dark:text-orange-400">
              미인증
            </span>
          )}
          {user.isSuperAdmin && (
            <span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary">
              관리자
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-muted-foreground">{user.organizationCount}</td>
      <td className="px-4 py-3 text-muted-foreground">
        {new Date(user.createdAt).toLocaleDateString()}
      </td>
      <td className="px-4 py-3 text-right">
        <Button type="button" variant="outline" size="sm" onClick={onClick}>
          상세
        </Button>
      </td>
    </tr>
  );
}

function UserDetailDialog({
  userId,
  onClose,
}: {
  userId: string;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const me = useAuthStore((s) => s.account);
  const isSelf = me?.id === userId;

  // 다이얼로그 내부의 인라인 메시지 (toast 인프라 미도입 — 나중에 toast 도입 시 교체).
  const [message, setMessage] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const detailQuery = useQuery<AdminUserDetail>({
    queryKey: ["admin-user-detail", userId],
    queryFn: () => getAdminUserDetail(userId),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    void queryClient.invalidateQueries({ queryKey: ["admin-user-detail", userId] });
  };

  const ok = (text: string): void => setMessage({ kind: "ok", text });
  const err = (e: unknown): void =>
    setMessage({ kind: "err", text: extractErrorMessage(e, "처리 실패") });

  const verifyMut = useMutation({
    mutationFn: () => adminVerifyEmail(userId),
    onSuccess: () => {
      ok("이메일 인증 처리됨");
      invalidate();
    },
    onError: err,
  });

  const revokeMut = useMutation({
    mutationFn: () => adminRevokeSessions(userId),
    onSuccess: (data) => {
      ok(`세션 ${data.revokedCount}개 강제 종료됨`);
      invalidate();
    },
    onError: err,
  });

  const suspendMut = useMutation({
    mutationFn: () => adminSuspend(userId),
    onSuccess: () => {
      ok("계정 비활성화됨");
      invalidate();
    },
    onError: err,
  });

  const unsuspendMut = useMutation({
    mutationFn: () => adminUnsuspend(userId),
    onSuccess: () => {
      ok("계정 활성화됨");
      invalidate();
    },
    onError: err,
  });

  const toggleAdminMut = useMutation({
    mutationFn: (grant: boolean) => adminToggleSuperAdmin(userId, grant),
    onSuccess: (data) => {
      ok(data.isSuperAdmin ? "관리자 권한 부여됨" : "관리자 권한 회수됨");
      invalidate();
    },
    onError: err,
  });

  const u = detailQuery.data;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-2xl rounded-lg border border-border bg-card p-6 shadow-lg max-h-[90vh] overflow-y-auto">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">사용자 상세</h2>
            {u && (
              <p className="mt-1 text-sm text-muted-foreground">
                {u.email}
                {u.name && ` · ${u.name}`}
              </p>
            )}
          </div>
          <button
            type="button"
            aria-label="닫기"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {detailQuery.isLoading && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> 불러오는 중…
          </p>
        )}

        {message && (
          <div
            className={cn(
              "mb-4 rounded-md border px-3 py-2 text-sm",
              message.kind === "ok"
                ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                : "border-destructive/30 bg-destructive/10 text-destructive",
            )}
          >
            {message.text}
          </div>
        )}

        {u && (
          <div className="space-y-5">
            {/* 상태 요약 */}
            <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              <Stat label="이메일 인증" value={u.emailVerified ? "✓" : "✗"} />
              <Stat
                label="활성 세션"
                value={String(u.activeSessions)}
              />
              <Stat
                label="조직 소속"
                value={`${u.organizationCount}곳`}
              />
              <Stat
                label="가입일"
                value={new Date(u.createdAt).toLocaleDateString()}
              />
            </div>

            {u.suspendedAt && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                <strong>비활성화됨</strong> ·{" "}
                {new Date(u.suspendedAt).toLocaleString()}
              </div>
            )}

            {/* 멤버십 */}
            <div>
              <h3 className="mb-2 text-sm font-medium">소속 조직</h3>
              {u.memberships.length === 0 ? (
                <p className="text-sm text-muted-foreground">소속된 조직이 없습니다.</p>
              ) : (
                <ul className="divide-y divide-border rounded-md border border-border text-sm">
                  {u.memberships.map((m) => (
                    <li
                      key={m.organizationId}
                      className="flex items-center justify-between px-3 py-2"
                    >
                      <div>
                        <p className="font-medium">{m.organizationName}</p>
                        <p className="font-mono text-xs text-muted-foreground">
                          {m.organizationSlug}
                        </p>
                      </div>
                      <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                        {m.role}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* 액션 */}
            <div>
              <h3 className="mb-2 text-sm font-medium">관리 액션</h3>
              <div className="grid gap-2 sm:grid-cols-2">
                {!u.emailVerified && (
                  <ActionButton
                    icon={<MailCheck className="h-4 w-4" />}
                    label="이메일 인증 강제 처리"
                    onClick={() => verifyMut.mutate()}
                    pending={verifyMut.isPending}
                  />
                )}
                {u.activeSessions > 0 && (
                  <ActionButton
                    icon={<LogOut className="h-4 w-4" />}
                    label={`세션 강제 종료 (${u.activeSessions})`}
                    onClick={() => revokeMut.mutate()}
                    pending={revokeMut.isPending}
                  />
                )}
                {!isSelf && !u.suspendedAt && (
                  <ActionButton
                    icon={<Ban className="h-4 w-4" />}
                    label="계정 비활성화"
                    variant="destructive"
                    onClick={() => {
                      if (confirm(`${u.email} 을 비활성화하시겠습니까?`)) {
                        suspendMut.mutate();
                      }
                    }}
                    pending={suspendMut.isPending}
                  />
                )}
                {u.suspendedAt && (
                  <ActionButton
                    icon={<CheckCircle2 className="h-4 w-4" />}
                    label="비활성화 해제"
                    onClick={() => unsuspendMut.mutate()}
                    pending={unsuspendMut.isPending}
                  />
                )}
                <ActionButton
                  icon={
                    u.isSuperAdmin ? (
                      <KeyRound className="h-4 w-4" />
                    ) : (
                      <ShieldCheck className="h-4 w-4" />
                    )
                  }
                  label={
                    u.isSuperAdmin
                      ? "관리자 권한 회수"
                      : "관리자 권한 부여"
                  }
                  onClick={() => {
                    const grant = !u.isSuperAdmin;
                    if (
                      confirm(
                        grant
                          ? `${u.email} 에게 관리자 권한을 부여하시겠습니까? 이 사용자는 모든 사용자 / 릴리스 / 가격을 관리할 수 있게 됩니다.`
                          : `${u.email} 의 관리자 권한을 회수하시겠습니까?`,
                      )
                    ) {
                      toggleAdminMut.mutate(grant);
                    }
                  }}
                  pending={toggleAdminMut.isPending}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-0.5 font-medium">{value}</p>
    </div>
  );
}

function ActionButton({
  icon,
  label,
  onClick,
  pending,
  variant = "outline",
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  pending: boolean;
  variant?: "outline" | "destructive";
}): JSX.Element {
  return (
    <Button
      type="button"
      variant={variant}
      onClick={onClick}
      disabled={pending}
      className={cn(
        "justify-start",
        variant === "destructive" && "text-destructive hover:text-destructive",
      )}
    >
      {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : icon}
      {label}
    </Button>
  );
}
